/**
 * Pure helpers for the verification form's financial adjustments (gdn-2457).
 *
 * The form seeds each input from the current snapshot and submits only the
 * fields the user actually changed:
 *   - untouched fields are omitted (the BE treats absence as "no change")
 *   - a seeded field cleared to empty submits an explicit `null`, which the
 *     BE records as clearing the stored value
 *   - the legacy bare `coinsurance` key is read for seeding but adjustments
 *     are always submitted under the canonical `coinsurance_cents`
 *
 * `predictCollectToday` mirrors the BE's member-obligation derivation
 * (utils/financials_merge.py) so the promoted "Collect today" field can show
 * what the verification will do before it is submitted.
 */

export interface FinancialFieldDef {
  field:
    | 'copayment'
    | 'member_obligation'
    | 'coinsurance'
    | 'deductible'
    | 'remaining_deductible'
    | 'oop_max'
    | 'remaining_oop_max';
  label: string;
  prefix: '$' | '%';
  /** Key used in the submitted financials patch. */
  apiKey: string;
}

export const FINANCIAL_FIELDS: FinancialFieldDef[] = [
  { field: 'copayment', label: 'Copay', prefix: '$', apiKey: 'copayment_cents' },
  {
    field: 'member_obligation',
    label: 'Collect today (est. member cost)',
    prefix: '$',
    apiKey: 'member_obligation_cents',
  },
  { field: 'coinsurance', label: 'Coinsurance', prefix: '%', apiKey: 'coinsurance_cents' },
  { field: 'deductible', label: 'Deductible', prefix: '$', apiKey: 'deductible_cents' },
  {
    field: 'remaining_deductible',
    label: 'Remaining deductible',
    prefix: '$',
    apiKey: 'remaining_deductible_cents',
  },
  { field: 'oop_max', label: 'Out-of-pocket max', prefix: '$', apiKey: 'oop_max_cents' },
  {
    field: 'remaining_oop_max',
    label: 'Remaining OOP max',
    prefix: '$',
    apiKey: 'remaining_oop_max_cents',
  },
];

/** Fields rendered above the "+ More fields" fold. */
export const PRIMARY_FIELDS: FinancialFieldDef['field'][] = [
  'copayment',
  'member_obligation',
];

/** Look up a field definition by name (never index positionally). */
function fieldDef(name: FinancialFieldDef['field']): FinancialFieldDef {
  const def = FINANCIAL_FIELDS.find((f) => f.field === name);
  if (!def) throw new Error(`Unknown financial field: ${name}`);
  return def;
}

export function centsToDollars(cents: unknown): string {
  if (typeof cents !== 'number' || !Number.isFinite(cents)) return '';
  return (cents / 100).toFixed(2);
}

function asNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * Percent value for coinsurance, tolerating every historical key. Canonical
 * key first — keep the precedence identical to `getFinancials` in
 * patient-coverages so the form seed and the coverage display agree.
 */
function coinsurancePercent(financials: Record<string, unknown>): number | null {
  return (
    asNumber(financials.coinsurance_cents) ??
    asNumber(financials.coinsurance) ??
    asNumber(financials.coinsurance_pct)
  );
}

/** Seed the form's input strings from a snapshot's financials dict. */
export function seedFinancialInputs(
  financials: Record<string, unknown>,
): Record<string, string> {
  const seeded: Record<string, string> = {};
  for (const def of FINANCIAL_FIELDS) {
    if (def.prefix === '$') {
      seeded[def.field] = centsToDollars(financials[def.apiKey]);
    } else {
      const pct = coinsurancePercent(financials);
      seeded[def.field] = pct !== null ? String(pct) : '';
    }
  }
  return seeded;
}

/**
 * Parse one input's raw string. `null` means empty (a clear when the field
 * was seeded); `undefined` means unparsable or negative — treat as untouched.
 */
function parseInput(def: FinancialFieldDef, raw: string): number | null | undefined {
  if (raw.trim() === '') return null;
  const n = Number.parseFloat(raw);
  if (!Number.isFinite(n) || n < 0) return undefined;
  return def.prefix === '$' ? Math.round(n * 100) : n;
}

/**
 * Diff the form state against its seeded values into the submitted patch.
 * Returns `null` when nothing changed.
 *
 * `forceInclude` fields are sent even when equal to the seed (or empty): the
 * BE treats the mere presence of `member_obligation_cents` as explicit
 * intent that suppresses re-derivation, so a user who touched "Collect
 * today" and kept the displayed amount actually keeps it.
 */
export function buildFinancialsPatch(
  initial: Record<string, string>,
  current: Record<string, string>,
  options?: { forceInclude?: FinancialFieldDef['field'][] },
): Record<string, number | null> | null {
  const force = new Set(options?.forceInclude ?? []);
  const patch: Record<string, number | null> = {};
  for (const def of FINANCIAL_FIELDS) {
    const before = parseInput(def, initial[def.field] ?? '');
    const after = parseInput(def, current[def.field] ?? '');
    if (after === undefined) continue; // unparsable — treat as untouched
    if (!force.has(def.field)) {
      if (before === after) continue;
      if (before === null && after === null) continue;
    }
    patch[def.apiKey] = after;
  }
  return Object.keys(patch).length > 0 ? patch : null;
}

/**
 * Label of the first edited field whose value cannot be submitted (negative
 * or unparsable), or null when every non-empty input is valid. Submission
 * must be blocked on a non-null result: silently dropping an invalid entry
 * would recreate the "override appeared to do nothing" failure mode.
 */
export function firstInvalidFinancialInput(
  current: Record<string, string>,
): string | null {
  for (const def of FINANCIAL_FIELDS) {
    const raw = (current[def.field] ?? '').trim();
    if (raw === '') continue;
    if (parseInput(def, raw) === undefined) return def.label;
  }
  return null;
}

// Mirrors _NON_COPAY_COST_SHARING_KEYS in the BE's financials_merge.py:
// signals that the obligation is not simply the copay.
const NON_COPAY_COST_SHARING_KEYS = [
  'coinsurance',
  'coinsurance_cents',
  'deductible_cents',
  'remaining_deductible_cents',
  'pre_deductible_member_obligation_cents',
  'post_deductible_member_obligation_cents',
  'pre_oop_max_member_obligation_cents',
  'post_oop_max_member_obligation_cents',
];

function isCopayOnly(financials: Record<string, unknown>): boolean {
  return NON_COPAY_COST_SHARING_KEYS.every((key) => {
    const value = financials[key];
    return value === null || value === undefined || value === 0;
  });
}

export interface CollectTodayPrediction {
  /** Predicted member obligation in cents; null = unknown / will clear. */
  cents: number | null;
  /**
   * - `explicit`: the user set the field themselves
   * - `derived`: will follow the adjusted copay
   * - `cleared`: can no longer be derived — falls back to "Verify with payer"
   * - `unchanged`: no obligation-driver changed
   */
  source: 'explicit' | 'derived' | 'cleared' | 'unchanged';
}

/**
 * Predict what member_obligation_cents the BE will persist for the current
 * form state. Applies the full dirty-field patch to the snapshot — exactly
 * like the BE merge — before running the derivation, so edits to any
 * cost-sharing field (deductible edits included) predict correctly. Must
 * stay in lockstep with financials_merge.py.
 */
export function predictCollectToday(
  financials: Record<string, unknown>,
  initial: Record<string, string>,
  current: Record<string, string>,
  moTouched: boolean,
): CollectTodayPrediction {
  if (moTouched) {
    const moDef = fieldDef('member_obligation');
    const explicit = parseInput(moDef, current[moDef.field] ?? '');
    return { cents: explicit === undefined ? null : explicit, source: 'explicit' };
  }

  // Untouched member_obligation is never in the patch, so what remains is
  // exactly what the BE will merge before deriving.
  const patch = buildFinancialsPatch(initial, current) ?? {};
  const merged: Record<string, unknown> = { ...financials };
  for (const [key, value] of Object.entries(patch)) {
    // undefined reads as "absent" everywhere below, mirroring the BE's
    // key deletion without a dynamic `delete`.
    merged[key] = value === null ? undefined : value;
    if (key === 'coinsurance_cents') {
      merged.coinsurance = undefined;
    }
  }

  const copayChanged = 'copayment_cents' in patch;
  const coinsuranceChanged = 'coinsurance_cents' in patch;
  const initialMo = asNumber(financials.member_obligation_cents);
  const initialCopay = asNumber(financials.copayment_cents);
  const newCopay = asNumber(merged.copayment_cents);
  const mergedMo = asNumber(merged.member_obligation_cents);

  if (mergedMo === null) {
    // Absent: the BE fills only the one safe case — copay-only cost sharing.
    if (newCopay !== null && isCopayOnly(merged)) {
      return { cents: newCopay, source: 'derived' };
    }
    return { cents: null, source: 'unchanged' };
  }

  if (!copayChanged && !coinsuranceChanged) {
    return { cents: mergedMo, source: 'unchanged' };
  }

  if (newCopay !== null && isCopayOnly(merged)) {
    return { cents: newCopay, source: 'derived' };
  }
  if (
    copayChanged &&
    !coinsuranceChanged &&
    newCopay !== null &&
    initialMo !== null &&
    initialMo === initialCopay
  ) {
    return { cents: newCopay, source: 'derived' };
  }
  return { cents: null, source: 'cleared' };
}
